import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon, Popup } from "semantic-ui-react";

import { AppointmentNotes, AppointmentPin, AppointmentStatusCell, DATE_FORMATS_FNS, getAppointmentStatus } from "components";
import { useIsAllLocationsSelected, usePermanentLocations, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient, usePermanentRealTimeUser } from "hooks";
import { CUSTOMER_TYPES, DMS_CAPABILITIES } from "models";
import { useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { DATE_ORDER_BY, WO_FILTERS } from "modules/Appointments";
import { SpecialIndicators } from "modules/Appointments/components";
import "modules/Appointments/components/AppointmentTable/AppointmentTable.scss";
import {
  AppointmentRow,
  AppointmentTableDateColumnCell,
  ColumnDataSelector,
  FilterIcons,
  ThreeDepartmentNoteIcon
} from "modules/Appointments/components/AppointmentTable/components";
import { useHeaderDropdownOptions } from "modules/Appointments/components/AppointmentTable/hooks";
import { setCarMakesToFilter, useCarMakesOptions, useCarMakesToFilter } from "modules/Appointments/hooks";
import { APPOINTMENTS_STORAGE_KEYS } from "modules/Appointments/storageKeys";
import { ACCESSOR_KEYS, determineAPKColor } from "modules/Appointments/util";
import { PATHS } from "router/paths";
import { isAppointmentWarrantyPinSignatureMissing } from "utils/appointmentUtils";
import { getCustomerName, getPreference, getUserNameInitials, setPreference } from "utils/common";
import { formatDate, isDate } from "utils/dateHelperFunctions";

import type { Appointment, Checklist } from "models";
import type { ColumnDataSelectorChildMethods } from "modules/Appointments/components/AppointmentTable/components";
import type { RefObject } from "react";

// TODO: find a solution to this any
const columnHelper = createColumnHelper<any>();

type AppointmentsTableProps = {
  appointments: Appointment[];
  isTodaysList: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  woColumnAccessor: WO_FILTERS;
  dateColumnAccessor: DATE_ORDER_BY;
  handleChangeWoColumnAccessor: (value: WO_FILTERS) => void;
  handleChangeDateColumnAccessor: (value: DATE_ORDER_BY) => void;
};

const ROW_HEIGHT = 36;

const getDefaultCustomerType = (defaultCustomer?: string) => {
  if (defaultCustomer === "owner") return CUSTOMER_TYPES.Owner;
  if (defaultCustomer === "contractor") return CUSTOMER_TYPES.Contractor;
  return CUSTOMER_TYPES.Driver;
};

export const AppointmentTable = ({
  appointments,
  isTodaysList,
  handleChangeWoColumnAccessor,
  handleChangeDateColumnAccessor,
  dateColumnAccessor,
  woColumnAccessor,
  containerRef
}: AppointmentsTableProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { isAllLocationsSelected } = useIsAllLocationsSelected();
  const { locations } = usePermanentLocations();
  const { selectedLocation } = useSelectedLocation();
  const carMakesOptions = useCarMakesOptions();
  const headerDropdownOptions = useHeaderDropdownOptions(carMakesOptions);
  const carMakesToFilter = useCarMakesToFilter();
  const { data: brands = [] } = useCarBrandsQuery();
  const history = useHistory();

  const defaultCustomer = locations.find(location => location.id === selectedLocation?.id)?.default_customer;

  const [customerColumnAccessor, setCustomerColumnAccessor] = useState(
    getPreference(APPOINTMENTS_STORAGE_KEYS.DriverPreference, getDefaultCustomerType(defaultCustomer))
  );
  const [lastUserColumnAccessor, setLastUserColumnAccessor] = useState(getPreference(ACCESSOR_KEYS.LastUserColumn, headerDropdownOptions.lastUser[0].key));

  const lastUserColumnRef = useRef<ColumnDataSelectorChildMethods>(null);

  const virtualizerOptions = useMemo(
    () => ({
      count: appointments.length,
      getScrollElement: () => containerRef.current,
      estimateSize: () => ROW_HEIGHT,
      overscan: 30,
      scrollBehavior: "smooth",
      initialRect: {
        width: 1,
        height: 1
      }
    }),
    [appointments.length]
  );

  const rowVirtualizer = useVirtualizer(virtualizerOptions);

  const virtualizerHeight = rowVirtualizer.getTotalSize();
  const shouldShowScheduledError = !selectedLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.ScheduledInOut) && user?.list_appointments_by_scheduled_range;
  const tbodyHeight = shouldShowScheduledError ? "500px" : `${virtualizerHeight}px`;

  const getHiddenColumns = () => {
    return { apk: !!selectedLocation?.apk_visible };
  };

  const handleMultipleCarMakeFilter = useCallback(
    (carMake: string) => {
      if (carMake === "All") return setCarMakesToFilter([]);

      const updatedCarMakesToFilter = carMakesToFilter.includes(carMake) ? carMakesToFilter.filter(cm => cm !== carMake) : [...carMakesToFilter, carMake];
      setCarMakesToFilter(updatedCarMakesToFilter);
    },
    [carMakesToFilter]
  );

  const getUser = useCallback(
    (appointment: Appointment) => {
      if (lastUserColumnAccessor === "my_appointments") {
        const userName = `${user?.first_name} ${user?.last_name}`;

        return {
          name: appointment.customer_owner_name === userName ? appointment.customer_owner_name : "",
          profilePicture: appointment.customer_owner_profile_picture,
          lastUpdatedAt: null
        };
      }

      if (lastUserColumnAccessor === "customer_owner") {
        return { name: appointment.customer_owner_name, profilePicture: appointment.customer_owner_profile_picture, lastUpdatedAt: null };
      }

      if (lastUserColumnAccessor === "receptionist_changed") {
        return {
          name: appointment.last_receptionist_name,
          profilePicture: appointment.last_receptionist_profile_picture,
          lastUpdatedAt: null
        };
      }

      return {
        name: appointment.last_user,
        profilePicture: appointment.last_user_profile_picture,
        lastUpdatedAt: appointment.last_user_at
      };
    },
    [lastUserColumnAccessor, user?.first_name, user?.last_name]
  );

  const handleChangeCustomerColumnAccessor = (value: CUSTOMER_TYPES) => {
    setPreference(APPOINTMENTS_STORAGE_KEYS.DriverPreference, value);
    setCustomerColumnAccessor(value);
  };

  const handleChangeLastUserColumnAccessor = (value: string) => {
    setPreference(ACCESSOR_KEYS.LastUserColumn, value);
    setLastUserColumnAccessor(value);
  };

  const handleRowClick = useCallback(
    async (appointment: Appointment) => {
      try {
        typedQueryClient.invalidateQueries(AppointmentDetailsKeys.view);
        history.push(`${PATHS.APPOINTMENTS}/${appointment.id}`);
      } catch {
        toast.error(t("v8_error_loading_appointment", "Something went wrong loading the appointment. Please try again"));
      }
    },
    [history, t]
  );

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor(row => row, {
        id: "wo",
        header: () => (
          <div className="appointment-table-wrapper pointer">
            <ColumnDataSelector
              trigger={
                <p className="carret-header-wrapper">
                  {t("v8_wo", "WO")} #{appointments.length}
                  <Icon className={`${woColumnAccessor !== WO_FILTERS.ALL ? "filters" : "filter"} table-dropdown-filters-icon`} size="small" />
                </p>
              }
              selected={woColumnAccessor}
              options={headerDropdownOptions.wo}
              setSelectedValue={handleChangeWoColumnAccessor}
            />
          </div>
        ),
        size: 105,
        cell: ({ row: { original: appointment } }) => (
          <div className="wo-nr-container">
            <AppointmentPin appointment={appointment} location={locations.find(location => location.id === appointment.dealer_location_id)} />

            <Popup
              basic
              content={appointment.wo_nr}
              trigger={
                <div className={`ellipsis ellipsis-wo-nr ${appointment.internal ? "internal-wo" : ""}`}>
                  <span className={appointment.internal ? "internal-wo-text" : ""}>
                    <bdi>{appointment.wo_nr}</bdi>
                  </span>
                </div>
              }
            />
          </div>
        )
      }),

      columnHelper.accessor(
        row => {
          const key = dateColumnAccessor.replace("_asc", "").replace("_desc", "") as keyof typeof row;
          const date = row[key] ? String(row[key]) : null;
          return { date, isPinned: row.is_pinned };
        },
        {
          id: "date",
          cell: info => <AppointmentTableDateColumnCell dateColumnAccessor={dateColumnAccessor} value={info.getValue()} isTodaysList={isTodaysList} />,
          header: () => {
            const isAppointmentDate = dateColumnAccessor === DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING || dateColumnAccessor === DATE_ORDER_BY.APPOINTMENT_DATE_DESCENDING;
            return (
              <div className="appointment-table-wrapper pointer">
                <ColumnDataSelector
                  trigger={
                    <div className="arrow-header-wrapper">
                      <p>{isAppointmentDate ? t("v8_appointment_date", "Appointment Date") : t("v8_scheduled_out", "Scheduled Out")}</p>
                      <Icon className="caret down" />
                    </div>
                  }
                  selected={dateColumnAccessor}
                  options={headerDropdownOptions.date}
                  setSelectedValue={handleChangeDateColumnAccessor}
                />
              </div>
            );
          },
          size: 140
        }
      ),
      columnHelper.accessor(row => row, {
        id: "reg_nr",
        header: () => t("v8_reg", "Reg #"),
        size: 100,
        cell: ({ row: { original } }) => (
          <Popup
            basic
            content={original.reg_number}
            trigger={
              <p className="ellipsis">
                <Link className="AppointmentTable-link" to={`${PATHS.CAR_DETAIL}/${original.car_id}`} onClick={e => e.stopPropagation()}>
                  {original.is_lease_company ? <Icon name="building" /> : <Icon name="info circle" />}
                  {original.reg_number}
                </Link>
              </p>
            }
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "apk",
        header: () => "APK",
        size: 100,
        cell: ({ row: { original } }) => {
          const formattedDate = original.car_apk_date ? formatDate(original.car_apk_date, DATE_FORMATS_FNS.dateMonthYearShort) : "";
          const apkColorClass = original.car_apk_date ? determineAPKColor(original.car_apk_date) : "";

          return <span className={apkColorClass}>{formattedDate}</span>;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "customer_name",
        header: () => {
          let title = "driver";
          if (customerColumnAccessor === CUSTOMER_TYPES.Owner) title = "owner";
          else if (customerColumnAccessor === CUSTOMER_TYPES.Contractor) title = "contractor";

          return (
            <div className="appointment-table-wrapper pointer">
              <ColumnDataSelector
                trigger={
                  <p className="arrow-header-wrapper">
                    {t(`v8_${title}`, title[0].toUpperCase() + title.substring(1))}
                    <Icon className="caret down" />
                  </p>
                }
                selected={customerColumnAccessor}
                options={headerDropdownOptions.customer}
                setSelectedValue={handleChangeCustomerColumnAccessor}
              />
            </div>
          );
        },
        size: 100,
        cell: ({ row: { original } }) => (
          <p
            className={`ellipsis  customer-ellipsis ${isAppointmentWarrantyPinSignatureMissing(original, brands, selectedLocation) ? "-warranty-signature-missing" : original.check_in_com_answered ? "-customer-sign-wo" : ""}`}
          >
            {getCustomerName({ appointment: original, customerType: customerColumnAccessor, showTitle: false, t: t })}
          </p>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "make",
        header: () => (
          <div className="appointment-table-wrapper pointer">
            <ColumnDataSelector
              trigger={
                <p className="carret-header-wrapper">
                  {t("v8_make", "Make")}
                  <Icon className={`${carMakesToFilter.length ? "filters" : "filter"} table-dropdown-filters-icon`} size="small" />
                </p>
              }
              selected={carMakesToFilter}
              multiple
              options={headerDropdownOptions.carMake}
              setSelectedValue={handleMultipleCarMakeFilter}
            />
          </div>
        ),
        size: 80,
        cell: ({ row: { original } }) => <p className="ellipsis small">{original.car_make}</p>
      }),
      columnHelper.accessor(row => row, {
        id: "model",
        header: () => t("v8_model", "Model"),
        cell: ({ row: { original } }) => (
          <Popup hoverable content={original.car_model} trigger={<p className="ellipsis ellipsis-model model-text">{original.car_model}</p>} />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "note",
        header: () => t("v8_note", "Note"),
        cell: ({ row: { original: appointment } }) => (
          <div className="NoteColumnCell">
            {selectedLocation?.is_three_departments_enabled && <ThreeDepartmentNoteIcon appointment={appointment} />}
            <AppointmentNotes notes={appointment.notes} carInShop={appointment.car_in_shop} carOutOfShop={appointment.car_out_of_shop} />
          </div>
        ),
        size: 80
      }),

      columnHelper.accessor(row => row, {
        id: "circle",
        size: 160,
        header: () => user && <SpecialIndicators />,
        cell: ({ row: { original } }) =>
          selectedLocation ? <FilterIcons appointment={original} location={locations.find(location => location.id === original.dealer_location_id)} /> : null
      }),

      columnHelper.accessor(row => row, {
        id: "last_timestamp",
        header: () => t("v8_updated", "Updated"),
        size: 60,
        cell: ({ row: { original } }) =>
          original.last_timestamp && (isDate(original.last_timestamp) || !original.last_timestamp.startsWith("0001-01-01T00:00:00"))
            ? formatDate(original.last_timestamp, DATE_FORMATS_FNS.hoursMinutes)
            : ""
      }),

      columnHelper.accessor(row => row, {
        id: "last_user",
        header: () => (
          <div
            className="appointment-table-user-container"
            onClick={() => {
              lastUserColumnRef.current?.toggleDropdown();
            }}
          >
            <ColumnDataSelector
              trigger={
                <div className="arrow-header-wrapper">
                  <div className="appointment-table-user appointemnt-table-user-head ">
                    <p className="text ">{getUserNameInitials(lastUserColumnAccessor)}</p>
                  </div>
                  <Icon className="caret down " />
                </div>
              }
              ref={lastUserColumnRef}
              selected={lastUserColumnAccessor}
              options={headerDropdownOptions.lastUser}
              setSelectedValue={handleChangeLastUserColumnAccessor}
            />
          </div>
        ),
        size: 60,
        cell: ({ row: { original } }) => {
          const value = getUser(original);
          const status = getAppointmentStatus(original.appointment_status_identifier, selectedLocation?.statuses);

          if (!value.name) return null;

          if (lastUserColumnAccessor === "assigned_to") {
            return (
              <Popup
                flowing
                content={
                  <div className="appointment-table-user-popup">
                    {original.planning_work_start && (
                      <div className="appointment-table-user-popup-bottom-timestamp">
                        <p>{t("v8_planning_work_start", "Planning work start")}</p>
                        {formatDate(original.planning_work_start, DATE_FORMATS_FNS.dateMonthYearTime)}
                      </div>
                    )}
                    {original.planning_work_stop && (
                      <div className="appointment-table-user-popup-bottom-timestamp">
                        <p>{t("v8_planning_work_stop", "Planning work stop")}</p>
                        {formatDate(original.planning_work_stop, DATE_FORMATS_FNS.dateMonthYearTime)}
                      </div>
                    )}
                  </div>
                }
                trigger={
                  original.planning_mechanic && (
                    <div className="appointment-table-user appointment-table-last-user">
                      <p className="text">{getUserNameInitials(`${original.planning_mechanic.first_name} ${original.planning_mechanic.last_name}`)}</p>
                    </div>
                  )
                }
                disabled={!original.planning_work_stop && !original.planning_work_start}
              />
            );
          }

          return (
            <Popup
              flowing
              content={
                <div className="appointment-table-user-popup">
                  {" "}
                  <div className="appointment-table-user-popup-top">
                    {value.profilePicture ? (
                      <img src={value.profilePicture} className="appointment-table-user-popup-top-initials-image" />
                    ) : (
                      <div className="appointment-table-user-popup-top-initials">{getUserNameInitials(value.name)}</div>
                    )}
                    <div className="appointment-table-user-popup-top-name">{value.name}</div>
                  </div>
                  {value.lastUpdatedAt && (
                    <div className="appointment-table-user-popup-bottom">
                      {value.lastUpdatedAt && (
                        <div className="appointment-table-user-popup-bottom-timestamp">{formatDate(value.lastUpdatedAt, DATE_FORMATS_FNS.yearMonthDatehoursMinutes)}</div>
                      )}
                      <div className="appointment-table-user-popup-bottom-status">{status.name}</div>
                    </div>
                  )}
                </div>
              }
              trigger={
                <div className="appointment-table-user appointment-table-last-user">
                  {value.profilePicture ? <img src={value.profilePicture} className="image" /> : <p className="text">{getUserNameInitials(value.name)}</p>}
                </div>
              }
            />
          );
        }
      }),
      columnHelper.accessor(row => row, {
        id: "check_list",
        header: () => t("v8_checklists", "Checklists"),
        cell: ({ row: { original } }) => (
          <div className="appointment-table-wrapper">
            {original.checklists?.map((value: Checklist, index: number) => {
              if (index < 4)
                return (
                  <Popup
                    key={`${value.name}_${index}`}
                    content={value.name}
                    trigger={
                      <div key={`checklist-${index}`} className="appointment-table-checklist">
                        {value.name.substring(0, 1)}
                      </div>
                    }
                  />
                );
              if (index === 4)
                return (
                  <div key={`checklist-${index}`}>
                    <p>...</p>
                  </div>
                );
              return null;
            })}
          </div>
        ),
        size: 90
      }),
      columnHelper.accessor(
        row => ({
          id: row.appointment_status_identifier,
          customcom_status: row.customer_communication?.status,
          car_check_paused_at: row.car_check_paused_at,
          car_out_of_shop: row.car_out_of_shop,
          car_in_shop: row.car_in_shop
        }),
        {
          id: "status",
          header: () => t("v8_status", "Status"),
          size: 55,
          cell: info => <AppointmentStatusCell shadow={false} value={info.getValue()} statuses={selectedLocation?.statuses} />
        }
      )
    ];

    if (user?.location_column_visible && isAllLocationsSelected) {
      const locationColumn = columnHelper.accessor(row => row, {
        id: "location",
        header: () => <p className="arrow-header-wrapper">{t("v8_location", "Location")}</p>,
        size: 100,
        cell: ({ row: { original } }) => {
          const locationName = locations.find(element => element.id === original.dealer_location_id)?.name;
          if (!locationName) return null;

          return (
            <Popup
              basic
              content={locationName}
              trigger={
                <div className="wo-nr-container">
                  <p className="ellipsis">{locationName}</p>
                </div>
              }
            />
          );
        }
      });

      cols.splice(1, 0, locationColumn);
    }

    return cols;
  }, [
    appointments.length,
    woColumnAccessor,
    dateColumnAccessor,
    customerColumnAccessor,
    lastUserColumnAccessor,
    carMakesToFilter,
    headerDropdownOptions,
    selectedLocation,
    brands,
    user,
    isTodaysList,
    isAllLocationsSelected,
    locations,
    getUser,
    handleMultipleCarMakeFilter,
    handleChangeWoColumnAccessor,
    handleChangeDateColumnAccessor,
    t
  ]);

  const table = useReactTable({
    data: appointments,
    columns,
    enableHiding: true,
    initialState: {
      columnVisibility: getHiddenColumns()
    },
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: { apk: !!selectedLocation?.apk_visible, date: selectedLocation?.date_visible as boolean }
    }
  });

  const renderRow = useCallback(
    (virtualRow: { index: number; start: number; size: number }) => {
      const row = table.getRowModel().rows[virtualRow.index];

      return <AppointmentRow key={row.id} row={row} virtualRowStart={virtualRow.start} virtualRowSize={virtualRow.size} onRowClick={handleRowClick} columns={columns} />;
    },
    [columns, handleRowClick]
  );

  return (
    <table className="ReactTable AppointmentsTable">
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th style={{ width: header.column.columnDef.size }} className={`${header.id}_th`} key={header.id}>
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody
        style={{
          position: "relative",
          height: tbodyHeight,
          contain: "strict"
        }}
      >
        {shouldShowScheduledError ? (
          <tr>
            <td colSpan={table.getVisibleLeafColumns().length} className="text-center message-center">
              <div className="message-center-content">
                {t(
                  "v8_scheduled_range_not_supported",
                  "This location cannot list appointments by scheduled range, please set 'List appointments by' to 'Appointment date' in your account page."
                )}
              </div>
            </td>
          </tr>
        ) : (
          <>
            {rowVirtualizer.getVirtualItems().map(renderRow)}

            {!table.getRowModel().rows.length && (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length} className="text-center message-center">
                  <div className="message-center-content">{t("v8_no_records_found", "No Records found")}</div>
                </td>
              </tr>
            )}
          </>
        )}
      </tbody>
    </table>
  );
};
